import {Component, OnDestroy, OnInit} from '@angular/core';
import {BreadcrumbModel} from '@shared/models/breadcrumb.model';
import {Observable, Subject} from 'rxjs';
import {InvalidateSubject} from '@shared/helpers/invalidate.subject';
import {ActivatedRoute, Router} from '@angular/router';
import {DialogService} from '@shared/services/dialog-service';
import {CommandService} from '@core/services/command.service';
import {select, Store} from '@ngrx/store';
import {ParticleService} from '@core/services/particle.service';
import {getUserInfo} from '@shared/store/selectors/security.selector';
import {take, takeUntil} from 'rxjs/operators';
import {SecurityState} from '@shared/store/reducers/security.reducer';
import {DialogModel} from '@shared/models/dialog.model';
import {PlatformCommand} from '@shared/commands/command-base';
import {Api} from '@shared/constants/common-field.constants';
import {CreateSubAccountCommand, DeleteSubAccountCommand} from '../../commands/firm.commands';

@Component({
    standalone: false,
    selector: 'particle-sub-accounts',
  templateUrl: './sub-accounts.component.html',
  styleUrls: ['./sub-accounts.component.scss']
})
export class SubAccountsComponent implements OnInit, OnDestroy {
  sourceKey: string;
  breadcrumbs: BreadcrumbModel[];
  ngUnsubscribe: Subject<void> = new Subject<void>();
  invalidateSubject: InvalidateSubject;
  userInfo$: Observable<any>;
  private userInfo: any;

  constructor(private router: Router,
              private dialogService: DialogService,
              private commandService: CommandService,
              private activatedRoute: ActivatedRoute,
              private particleService: ParticleService,
              private securityStore: Store<SecurityState>) {
    this.sourceKey = 'SubAccount';
    this.invalidateSubject = new InvalidateSubject();
  }

  ngOnInit() {
    this.userInfo$ = this.securityStore.pipe(select(getUserInfo), takeUntil(this.ngUnsubscribe));
    this.userInfo$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(state => {
      if (state) {
        this.userInfo = state;
        this.breadcrumbs = [
          <BreadcrumbModel>{
            id: 'Company',
            title: this.userInfo.comanyName,
            type: 'company',
            relativeUrl: 'sub-accounts'
          }
        ];
      }
    });
  }

  createSubAccount() {
    const dialogModel = DialogModel.create('Create Sub Account', 'AddSubAccountDialogComponent', {}, '600px', '800px');
    this.dialogService.showOkCancelDialog(dialogModel).pipe(take(1)).subscribe(state => {
      if (state.action === 'OK') {
        const command = new CreateSubAccountCommand(state.data.input.accountName, state.data.input.description);
        this.commandService.executeCommand(command).pipe(take(1)).subscribe(commandState => {
          this.invalidateSubject.invalidate();
        });
      }
    });
  }

  onGridCellValueChange(event: any) {
    console.log('onGridCellValueChange', event);
    switch (event.fieldDefinition.key) {
      case 'AccountId':
      case 'Users':
        this.router.navigate([`${event.item.id}`], {relativeTo: this.activatedRoute});
        break;
    }

    if (event.action && event.action.id === 'Delete') {
      this.deleteSubAccount(event);
    }
    if (event.action && event.action.id === 'Detach') {
      this.detachSubAccount(event);
    }
  }

  deleteSubAccount(event) {
    this.dialogService.showYesNoDialog('Delete Sub Account', 'Are you sure you want to delete this sub account?').pipe(takeUntil(this.ngUnsubscribe)).subscribe(result => {
      if (result.action === 'OK') {
        const itemIds = [event.item.id];
        const command = new DeleteSubAccountCommand(itemIds);
        this.commandService.executeCommand(command).pipe(takeUntil(this.ngUnsubscribe)).subscribe(commandState => {
          this.invalidateSubject.invalidate();
        });
      }
    });
  }

  detachSubAccount(event) {
    this.dialogService.showYesNoDialog(
      'Detach Sub Account',
      'Detaching removes the firm link from this sub account’s company record. This action cannot be undone. Do you want to continue?'
    ).pipe(takeUntil(this.ngUnsubscribe)).subscribe(result => {
      if (result.action === 'OK') {
        const command = new PlatformCommand('DetachSubAccountCommand', Api.AdminPanel);
        command.setValue('SubAccountIds', [event.item.id]);
        this.commandService.executePlatformCommand(command).pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => {
          this.invalidateSubject.invalidate();
        });
      }
    });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
